import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { FaSync, FaCheckCircle, FaTimes, FaRedoAlt } from 'react-icons/fa';

/**
 * Listens for electron-updater events (forwarded by main.js over the preload
 * bridge) and shows a toast matching the app's theme:
 *   • while downloading  → "Downloading update… NN%"
 *   • when ready         → "Update vX.Y.Z is ready — Restart now"
 * Clicking Restart calls installUpdate() → autoUpdater.quitAndInstall().
 *
 * No-op outside Electron (web build).
 */

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const Toast = styled.div`
  /* !important pins the toast to the viewport's bottom-right even if some
     ancestor (transform / filter / will-change / contain) creates a CSS
     containing block that would otherwise trap the fixed positioning. The
     portal renders this into document.body so it shouldn't matter, but
     belt-and-braces in case a future ancestor changes the math. */
  position: fixed !important;
  top: auto !important;
  left: auto !important;
  bottom: 22px !important;
  right: 22px !important;
  z-index: 2147483000;
  width: 320px;
  background: ${p => p.theme?.colors?.background?.paper || '#1E1E1E'};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 1.1rem 1.2rem;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.45);
  font-family: ${p => p.theme?.typography?.fontFamily || "'Inter', 'Poppins', 'Roboto', sans-serif"};
  animation: ${slideIn} 0.25s ease;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-bottom: 0.6rem;
`;

const IconCircle = styled.div`
  width: 2.1rem;
  height: 2.1rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1rem;
  color: #fff;
  background: ${p => (p.$ready
    ? (p.theme?.colors?.successGradient || 'linear-gradient(135deg, #10b981 0%, #34d399 100%)')
    : (p.theme?.colors?.primaryGradient || 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'))};
  box-shadow: 0 0 0 4px ${p => (p.$ready
    ? (p.theme?.colors?.successGlow || 'rgba(16, 185, 129, 0.25)')
    : (p.theme?.colors?.primaryGlow || 'rgba(20, 184, 166, 0.25)'))};

  svg {
    animation: ${p => (p.$spin ? spin : 'none')} 1.4s linear infinite;
  }
`;

const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 0.92rem;
  font-weight: 700;
  color: ${p => p.theme?.colors?.text?.primary || '#fff'};
`;

const Sub = styled.div`
  font-size: 0.78rem;
  color: ${p => p.theme?.colors?.text?.secondary || 'rgba(255,255,255,0.6)'};
  margin-top: 1px;
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  color: ${p => p.theme?.colors?.text?.secondary || 'rgba(255,255,255,0.5)'};
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0.2rem;
  flex-shrink: 0;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: ${p => p.theme?.colors?.text?.primary || '#fff'};
  }
`;

const ProgressWrap = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  height: 8px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: ${p => p.theme?.colors?.primaryGradient || 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'};
  transition: width 0.3s ease;
  width: ${p => p.$pct}%;
`;

const ProgressPct = styled.div`
  margin-top: 6px;
  font-size: 0.74rem;
  color: ${p => p.theme?.colors?.text?.secondary || 'rgba(255,255,255,0.55)'};
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 0.85rem;
`;

const LaterBtn = styled.button`
  padding: 0.45rem 0.85rem;
  border-radius: 9px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: transparent;
  color: ${p => p.theme?.colors?.text?.secondary || 'rgba(255,255,255,0.75)'};
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    color: ${p => p.theme?.colors?.text?.primary || '#fff'};
  }
`;

const RestartBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 1rem;
  border-radius: 9px;
  border: none;
  background: ${p => p.theme?.colors?.primaryGradient || 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'};
  color: #fff;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 16px ${p => p.theme?.colors?.primaryGlow || 'rgba(20, 184, 166, 0.35)'};
  transition: transform 0.12s ease, filter 0.12s ease;

  &:hover { filter: brightness(1.08); transform: translateY(-1px); }
  &:active { transform: translateY(0); }
`;

const UpdateNotification = () => {
  const api = typeof window !== 'undefined' ? window.electronAPI : null;
  const [progress, setProgress] = useState(null); // number | null
  const [readyVersion, setReadyVersion] = useState(null); // string | null
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!api?.isElectron) return;

    api.onUpdateProgress?.((info) => {
      if (readyVersion) return; // already downloaded
      setProgress(typeof info?.percent === 'number' ? info.percent : null);
    });
    api.onUpdateDownloaded?.((info) => {
      setProgress(null);
      setReadyVersion(info?.version || '');
      setDismissed(false);
    });

    return () => {
      api.removeAllListeners?.('update-download-progress');
      api.removeAllListeners?.('update-downloaded');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!api?.isElectron) return null;
  const showDownloading = progress !== null && readyVersion === null;
  // Sticky: once an update is downloaded, the toast stays on screen until
  // the user clicks Restart now (which fires api.installUpdate → quitAndInstall).
  // Dismissing via Later / X used to hide it, but staff were tapping Later
  // reflexively and never restarting, so counters ran outdated bundles for days.
  const showReady = readyVersion !== null;
  if (!showDownloading && !showReady) return null;

  // Render the toast through a portal directly into document.body so it
  // escapes any ancestor that may have created a CSS containing block
  // (transform / filter / will-change / contain on a parent). Otherwise a
  // styled wrapper somewhere up the tree can trap `position: fixed` and the
  // toast snaps to top-left of that wrapper instead of the viewport.
  if (typeof document === 'undefined' || !document.body) return null;

  const node = showDownloading ? (
    <Toast>
      <Header>
        <IconCircle $spin>
          <FaSync />
        </IconCircle>
        <TitleBlock>
          <Title>Downloading update…</Title>
          <Sub>Resto360 POS will be ready to restart shortly</Sub>
        </TitleBlock>
      </Header>
      <ProgressWrap>
        <ProgressFill $pct={Math.round(progress)} />
      </ProgressWrap>
      <ProgressPct>{Math.round(progress)}%</ProgressPct>
    </Toast>
  ) : (
    <Toast>
      <Header>
        <IconCircle $ready>
          <FaCheckCircle />
        </IconCircle>
        <TitleBlock>
          <Title>Update {readyVersion ? `v${readyVersion} ` : ''}ready</Title>
          <Sub>Restart to apply the latest version</Sub>
        </TitleBlock>
      </Header>
      <Actions>
        <RestartBtn type="button" onClick={() => api.installUpdate?.()}>
          <FaRedoAlt size={11} />
          Restart now
        </RestartBtn>
      </Actions>
    </Toast>
  );

  return createPortal(node, document.body);
};

export default UpdateNotification;
